<template>
  <transition name="modal-fade">
    <div v-if="show" class="smart-narrative-overlay" @click.self="closeModal">
      <div class="smart-narrative-popup" :class="{ 'smart-summary-popup': useSummaryPresentation }">
        <!-- Header -->
        <div v-if="!useSummaryPresentation" class="sn-header">
          <h3 class="sn-title">SMART NARRATIVE</h3>
          <button class="sn-close-btn" @click="closeModal" aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <!-- Content -->
        <div class="sn-body" :class="{ 'sn-body-html': isHtmlResponse || useSummaryPresentation }">
          <!-- Loading State -->
          <div v-if="loading" class="loading-state smart-summary-loading">
            <div class="spinner"></div>
            <span>Generating smart narrative...</span>
            <div class="summary-loading-card" aria-hidden="true">
              <div class="summary-loading-header">
                <div></div>
                <span></span>
              </div>
              <div class="summary-loading-kpi"></div>
              <div class="summary-loading-bar"></div>
              <div class="summary-loading-lines">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="error-state">
            <p>{{ error }}</p>
            <button @click="generateAnalysis" class="retry-btn">Retry</button>
          </div>

          <!-- Analysis Content -->
          <div v-else class="analysis-content">
            <article v-if="smartNarrativeData" class="smart-summary-card">
              <header class="summary-card-header">
                <div class="summary-card-heading">
                  <h4>{{ smartNarrativeData.header.indicator_name }}</h4>
                  <span>{{ smartNarrativeData.header.sub_header }}</span>
                </div>
                <div class="summary-card-badge">{{ smartNarrativeData.header.badge_text }}</div>
              </header>

              <section class="summary-kpi-section">
                <div class="summary-kpi-value">
                  <strong>{{ smartNarrativeData.kpi_section.value }}</strong>
                  <span>{{ smartNarrativeData.kpi_section.unit }}</span>
                </div>
                <div class="summary-kpi-meta">
                  <p>{{ smartNarrativeData.kpi_section.metadata_label }}</p>
                  <span>{{ smartNarrativeData.kpi_section.metadata_value }}</span>
                </div>
              </section>

              <section class="summary-target-section">
                <div class="summary-target-status">
                  <span :class="targetStatusClass">{{ smartNarrativeData.target_section.status_label }}</span>
                </div>
                <div class="summary-progress-track">
                  <div class="summary-progress-fill" :style="progressStyle"></div>
                </div>
                <div class="summary-target-label">
                  {{ smartNarrativeData.target_section.target_label }}: {{ smartNarrativeData.target_section.target_value }}
                </div>
              </section>

              <section class="summary-insight-box">
                <div class="summary-insight-icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <ul>
                  <li
                    v-for="(point, index) in smartNarrativeData.insight_box.bullet_points"
                    :key="`insight-${index}`"
                  >
                    <span aria-hidden="true">&bull;</span>
                    <p>{{ point }}</p>
                  </li>
                  <li v-if="smartNarrativeData.insight_box.health_implication">
                    <span aria-hidden="true">&bull;</span>
                    <p><strong>{{ smartNarrativeData.insight_box.health_implication }}</strong></p>
                  </li>
                </ul>
              </section>

              <footer class="summary-card-footer">
                <div>
                  <p>{{ smartNarrativeData.footer.source_label }}</p>
                  <div class="summary-chip-list">
                    <span
                      v-for="source in smartNarrativeData.footer.source_chips"
                      :key="`source-${source}`"
                      class="summary-chip summary-chip-muted"
                    >
                      {{ source }}
                    </span>
                  </div>
                </div>

                <div>
                  <p>{{ smartNarrativeData.footer.related_label }}</p>
                  <div class="summary-chip-list">
                    <span
                      v-for="indicator in smartNarrativeData.footer.related_chips"
                      :key="`indicator-${indicator}`"
                      class="summary-chip summary-chip-accent"
                    >
                      {{ indicator }}
                    </span>
                  </div>
                </div>

                <button class="summary-exit-btn" @click="closeModal">
                  <span>Exit Summary</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </footer>
            </article>

            <!-- Full HTML responses are rendered in an isolated frame to avoid app CSS conflicts. -->
            <iframe
              v-else-if="isHtmlResponse"
              class="html-narrative-frame"
              :srcdoc="htmlNarrative"
              title="Smart narrative"
              sandbox="allow-scripts"
            ></iframe>

            <!-- Structured content from parsed response -->
            <template v-else-if="parsedSections">
              <!-- Indicator Overview - always shown first -->
              <div v-if="indicatorOverview" class="section-block overview-section">
                <h4 class="section-title">{{ indicatorOverview.title }}</h4>
                <div class="section-content" v-html="formatSectionContent(indicatorOverview.content)"></div>
              </div>

              <!-- Remaining sections - shown when expanded -->
              <template v-if="isExpanded && remainingSections">
                <div
                  v-for="(section, index) in remainingSections"
                  :key="index"
                  class="section-block"
                >
                  <h4 class="section-title">{{ section.title }}</h4>
                  <div class="section-content" v-html="formatSectionContent(section.content)"></div>
                </div>
              </template>
            </template>

            <!-- Fallback: Legacy display for non-structured responses -->
            <template v-else>
              <div class="summary-paragraph" v-html="summaryParagraph"></div>
              <div v-if="isExpanded && detailedContent" class="detailed-content" v-html="detailedContent"></div>
            </template>
          </div>
        </div>

        <!-- Footer Button -->
        <div v-if="!useSummaryPresentation" class="sn-footer">
          <button
            v-if="!isExpanded && hasDetailedContent"
            @click="toggleExpand"
            class="action-btn"
          >
            Read More
          </button>
          <button
            v-else
            @click="closeModal"
            class="action-btn"
          >
            Okay
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import analysisService from '@/services/analysisService';
import SmartAnalysisDataService from '@/services/SmartAnalysisDataService';

export default {
  name: 'SmartNarrativeModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    values: {
      type: Object,
      required: true,
    },
    chartImage: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      analysis: null,
      error: null,
      isExpanded: false,
      parsedSections: null,
    };
  },
  computed: {
    useSummaryPresentation() {
      return this.loading || Boolean(this.smartNarrativeData);
    },
    smartNarrativeData() {
      return this.normalizeSmartNarrative(this.analysis);
    },
    progressStyle() {
      const progress = this.smartNarrativeData
        ? Number(this.smartNarrativeData.target_section.progress_percentage)
        : 0;
      const boundedProgress = Math.min(Math.max(progress || 0, 0), 100);

      return { width: `${boundedProgress}%` };
    },
    targetStatusClass() {
      const status = this.smartNarrativeData
        ? this.smartNarrativeData.target_section.status_label.toLowerCase()
        : '';

      return {
        'summary-status': true,
        'summary-status-warning': status.includes('below'),
        'summary-status-success': status.includes('met') || status.includes('above'),
      };
    },
    indicatorOverview() {
      if (!this.parsedSections) return null;
      return this.parsedSections.indicatorOverview;
    },
    remainingSections() {
      if (!this.parsedSections) return null;
      return this.parsedSections.remaining;
    },
    hasRemainingSections() {
      return this.remainingSections && this.remainingSections.length > 0;
    },
    // Legacy computed props for fallback
    summaryParagraph() {
      if (!this.analysis || typeof this.analysis !== 'string') return '';

      const paragraphs = this.analysis.split(/\n\n|\n/);
      const firstParagraph = paragraphs[0] || '';

      return this.formatText(firstParagraph);
    },
    detailedContent() {
      if (!this.analysis || typeof this.analysis !== 'string') return '';

      const paragraphs = this.analysis.split(/\n\n|\n/);
      if (paragraphs.length <= 1) return '';

      const rest = paragraphs.slice(1).join('\n');
      return this.formatText(rest);
    },
    hasDetailedContent() {
      if (this.isHtmlResponse || this.smartNarrativeData) return false;
      if (typeof this.analysis !== 'string') return false;
      return this.hasRemainingSections || (this.detailedContent && this.detailedContent.trim().length > 0);
    },
    isHtmlResponse() {
      return this.isHtmlResponseContent(this.analysisHtml);
    },
    analysisHtml() {
      return this.extractHtmlFromResponse(this.analysis);
    },
    htmlNarrative() {
      if (!this.isHtmlResponse) return '';
      const html = this.analysisHtml.trim();

      if (/<html[\s>]/i.test(html)) {
        return html;
      }

      return `
        <!DOCTYPE html>
        <html lang="en">
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <script src="https://cdn.tailwindcss.com"></scr${'ipt'}>
            <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet">
            <style>
              body {
                margin: 0;
                padding: 16px;
                background: #f8fafc;
                font-family: 'Plus Jakarta Sans', Arial, sans-serif;
              }
            </style>
          </head>
          <body>${html}</body>
        </html>
      `;
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.isExpanded = false;
        document.body.style.overflow = 'hidden'; // Prevent body scroll
        // Only generate analysis if we don't have one cached
        if (!this.analysis) {
          this.generateAnalysis();
        }
      } else {
        document.body.style.overflow = ''; // Restore body scroll
      }
    },
    // Reset analysis when any control panel value changes
    values: {
      handler() {
        this.analysis = null;
        this.parsedSections = null;
        this.error = null;
        this.isExpanded = false;
      },
      deep: true,
    },
  },
  methods: {
    normalizeSmartNarrative(content) {
      let parsedContent = content;

      if (!parsedContent) return null;

      if (typeof parsedContent === 'string') {
        const trimmedContent = parsedContent.trim();
        const fencedJson = trimmedContent.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
        const jsonText = fencedJson ? fencedJson[1].trim() : trimmedContent;

        try {
          parsedContent = JSON.parse(jsonText);
        } catch {
          return null;
        }
      }

      if (!parsedContent || typeof parsedContent !== 'object') return null;

      parsedContent = parsedContent.model_response
        || parsedContent.analysis
        || parsedContent.message
        || parsedContent;

      if (Array.isArray(parsedContent)) {
        parsedContent = parsedContent[0];
      }

      if (typeof parsedContent === 'string') {
        return this.normalizeSmartNarrative(parsedContent);
      }

      if (!parsedContent || typeof parsedContent !== 'object') return null;

      const normalized = {
        header: {
          indicator_name: '',
          sub_header: '',
          badge_text: 'Smart Summary',
          ...(parsedContent.header || {}),
        },
        kpi_section: {
          value: '',
          unit: '',
          metadata_label: '',
          metadata_value: '',
          ...(parsedContent.kpi_section || {}),
        },
        target_section: {
          status_label: '',
          target_label: 'National Target',
          target_value: '',
          progress_percentage: 0,
          ...(parsedContent.target_section || {}),
        },
        insight_box: {
          bullet_points: [],
          health_implication: '',
          ...(parsedContent.insight_box || {}),
        },
        footer: {
          source_label: 'TRIANGULATED SOURCES',
          source_chips: [],
          related_label: 'RELATED INDICATORS',
          related_chips: [],
          ...(parsedContent.footer || {}),
        },
      };

      const hasRequiredShape = normalized.header.indicator_name
        && normalized.kpi_section.value
        && normalized.insight_box;

      if (!hasRequiredShape) return null;

      normalized.insight_box.bullet_points = Array.isArray(normalized.insight_box.bullet_points)
        ? normalized.insight_box.bullet_points
        : [];
      normalized.footer.source_chips = Array.isArray(normalized.footer.source_chips)
        ? normalized.footer.source_chips
        : [];
      normalized.footer.related_chips = Array.isArray(normalized.footer.related_chips)
        ? normalized.footer.related_chips
        : [];

      return normalized;
    },
    extractHtmlFromResponse(content) {
      if (!content || typeof content !== 'string') return '';

      const trimmedContent = content.trim();
      const fencedHtml = trimmedContent.match(/^```(?:html)?\s*([\s\S]*?)\s*```$/i);

      return fencedHtml ? fencedHtml[1].trim() : trimmedContent;
    },
    isHtmlResponseContent(content) {
      if (!content || typeof content !== 'string') return false;
      return /<!doctype html|<html[\s>]|<body[\s>]|<(section|article|header|footer|main|div|ul|table|style|script)[\s>]/i.test(content);
    },
    parseModelResponse(text) {
      // Try to parse the model response into sections
      // Supports formats:
      // 1. Markdown h3: "### Section" (preferred - used with ## main title)
      // 2. Markdown h2: "## Section"
      // 3. Numbered: "1. **Section:**"

      const sections = [];
      let match;

      // First, try h3 markdown heading format (### Heading) - model uses this with ## as main title
      const h3Regex = /###\s*([^\n]+)\n([\s\S]*?)(?=\n###\s|$)/g;

      match = h3Regex.exec(text);
      while (match !== null) {
        const title = match[1].trim().replace(/\*\*/g, ''); // Remove any bold markers
        const content = match[2].trim();
        if (title && content) {
          sections.push({ title, content });
        }
        match = h3Regex.exec(text);
      }

      // If h3 parsing didn't work, try h2 format (## Heading)
      if (sections.length === 0) {
        const h2Regex = /##\s*([^\n]+)\n([\s\S]*?)(?=\n##\s|$)/g;

        match = h2Regex.exec(text);
        while (match !== null) {
          const title = match[1].trim().replace(/\*\*/g, '');
          const content = match[2].trim();
          if (title && content) {
            sections.push({ title, content });
          }
          match = h2Regex.exec(text);
        }
      }

      // If markdown parsing didn't work, try numbered format
      if (sections.length === 0) {
        const numberedRegex = /(\d+)\.\s*\*\*([^:*]+):\*\*\s*([\s\S]*?)(?=\n\d+\.\s*\*\*|$)/g;

        match = numberedRegex.exec(text);
        while (match !== null) {
          sections.push({
            number: match[1],
            title: match[2].trim(),
            content: match[3].trim(),
          });
          match = numberedRegex.exec(text);
        }
      }

      if (sections.length === 0) {
        // Fallback: couldn't parse, return null to use legacy display
        return null;
      }

      // Find Indicator Overview section (usually first)
      const overviewIndex = sections.findIndex((s) => s.title.toLowerCase().includes('indicator overview')
        || s.title.toLowerCase().includes('overview'));

      let indicatorOverview = null;
      let remaining = [...sections];

      if (overviewIndex !== -1) {
        indicatorOverview = sections[overviewIndex];
        remaining = sections.filter((_, i) => i !== overviewIndex);
      } else if (sections.length > 0) {
        // If no explicit overview, use first section
        indicatorOverview = sections[0];
        remaining = sections.slice(1);
      }

      return { indicatorOverview, remaining };
    },
    formatSectionContent(content) {
      if (!content) return '';
      return content
        // Format sub-bullets with dashes
        .replace(/^\s*-\s*\*\*([^:*]+):\*\*\s*/gm, '<div class="sub-item"><strong>$1:</strong> ')
        .replace(/^\s*-\s*/gm, '<div class="sub-item">• ')
        // Close div tags (simple approach)
        .replace(/(<div class="sub-item">[^<]*?)(?=<div|$)/g, '$1</div>')
        // Bold text
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        // Line breaks
        .replace(/\n/g, '<br>');
    },
    formatText(text) {
      if (!text) return '';
      return text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\n/g, '<br>');
    },
    toggleExpand() {
      this.isExpanded = true;
    },
    closeModal() {
      this.$emit('close');
    },
    async generateAnalysis() {
      this.loading = true;
      this.error = null;

      try {
        const compiledData = await SmartAnalysisDataService.compileAnalysisData(this.values);

        // console.log('--- SMART NARRATIVE PAYLOAD ---');
        // console.log(JSON.stringify(compiledData, null, 2));
        // console.log('-------------------------------');

        const result = await analysisService.generateChartAnalysis(
          this.values.indicator,
          this.chartImage || 'placeholder',
          compiledData,
        );

        this.analysis = result;
        this.parsedSections = this.normalizeSmartNarrative(result)
          || typeof result !== 'string'
          || this.isHtmlResponseContent(this.extractHtmlFromResponse(result))
          ? null
          : this.parseModelResponse(result);
      } catch (err) {
        console.error('Smart Narrative Error:', err);
        this.error = 'Failed to generate summary. Please try again.';
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    // Move modal to body to escape any positioned parent containers
    document.body.appendChild(this.$el);
  },
  beforeDestroy() {
    document.body.style.overflow = '';
    // Remove element from body if it exists
    if (this.$el && this.$el.parentNode === document.body) {
      document.body.removeChild(this.$el);
    }
  },
};
</script>

<style lang="scss" scoped>
.smart-narrative-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999 !important;
  padding: 1rem;
}

.smart-narrative-popup {
  background: white;
  border-radius: 8px;
  width: 100%;
  max-width: 550px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.smart-summary-popup {
  max-width: 408px;
  background: transparent;
  box-shadow: none;
}

.smart-summary-popup .sn-body {
  overflow: visible;
}

.sn-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background-color: #EBF3F2;
}

.sn-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  letter-spacing: 0.5px;
}

.sn-close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;

  &:hover {
    color: #333;
  }
}

.sn-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.sn-body-html {
  padding: 0;
}

.html-narrative-frame {
  width: 100%;
  height: clamp(520px, 72vh, 720px);
  border: 0;
  display: block;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 0;
  gap: 1rem;
  color: #666;
}

.smart-summary-loading {
  width: 100%;
  max-width: 381px;
  margin: 0 auto;
  padding: 0;
  color: #334155;
  animation: summaryEntrance 0.28s ease-out both;

  .spinner {
    width: 34px;
    height: 34px;
    border-width: 3px;
    border-top-color: #007d53;
  }

  > span {
    font-family: 'Plus Jakarta Sans', 'Work Sans', Arial, sans-serif;
    font-size: 0.8rem;
    font-weight: 700;
  }
}

.summary-loading-card {
  width: 100%;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
}

.summary-loading-header {
  display: flex;
  justify-content: space-between;
  gap: 0.85rem;
  padding: 1.05rem;
  background: #007d53;

  div,
  span {
    display: block;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.28);
    animation: summaryShimmer 1.25s ease-in-out infinite;
  }

  div {
    width: 58%;
    height: 1rem;
  }

  span {
    width: 6rem;
    height: 1.4rem;
  }
}

.summary-loading-kpi,
.summary-loading-bar,
.summary-loading-lines span {
  display: block;
  border-radius: 999px;
  background: linear-gradient(90deg, #e2e8f0 0%, #f8fafc 45%, #e2e8f0 100%);
  background-size: 220% 100%;
  animation: summaryShimmer 1.25s ease-in-out infinite;
}

.summary-loading-kpi {
  width: 42%;
  height: 1.7rem;
  margin: 1.05rem 1.05rem 0.85rem;
}

.summary-loading-bar {
  height: 0.65rem;
  margin: 0 1.05rem 1.25rem;
}

.summary-loading-lines {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  margin: 0 1.05rem 1.05rem;
  padding: 0.85rem;
  border: 1px solid rgba(0, 125, 83, 0.1);
  border-radius: 12px;
  background: rgba(0, 125, 83, 0.05);

  span {
    height: 0.8rem;

    &:nth-child(2) {
      width: 86%;
    }

    &:nth-child(3) {
      width: 70%;
    }
  }
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e0e0e0;
  border-top-color: #348481;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-state {
  text-align: center;
  padding: 2rem;
  color: #dc3545;
}

.retry-btn {
  margin-top: 1rem;
  padding: 0.5rem 1.5rem;
  background: none;
  border: 1px solid #dc3545;
  color: #dc3545;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #dc3545;
    color: white;
  }
}

.analysis-content {
  color: #333;
}

.smart-summary-card {
  width: 100%;
  max-width: 381px;
  margin: 0 auto;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
  overflow: hidden;
  font-family: 'Plus Jakarta Sans', 'Work Sans', Arial, sans-serif;
  animation: summaryEntrance 0.32s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.summary-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 1.05rem;
  background: #007d53;
  animation: summaryRiseIn 0.34s ease-out both;
}

.summary-card-heading {
  min-width: 0;

  h4 {
    margin: 0;
    color: #fff;
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.25;
    letter-spacing: 0;
  }

  span {
    display: block;
    margin-top: 0.25rem;
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.65rem;
    font-weight: 600;
    line-height: 1.2;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
}

.summary-card-badge {
  flex: 0 0 auto;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  animation: summaryPulse 1.8s ease-in-out 0.4s 2;
}

.summary-kpi-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.85rem;
  padding: 1.05rem 1.05rem 0.425rem;
  animation: summaryRiseIn 0.34s ease-out 0.05s both;
}

.summary-kpi-value {
  display: flex;
  align-items: baseline;
  min-width: 0;

  strong {
    color: #0f172a;
    font-size: 1.6rem;
    font-weight: 800;
    line-height: 1;
    letter-spacing: 0;
  }

  span {
    margin-left: 0.25rem;
    color: #64748b;
    font-size: 0.65rem;
    font-weight: 500;
    line-height: 1.2;
  }
}

.summary-kpi-meta {
  text-align: right;

  p {
    margin: 0;
    color: #94a3b8;
    font-size: 0.65rem;
    font-weight: 700;
    line-height: 1.1;
    text-transform: uppercase;
    letter-spacing: 0;
  }

  span {
    display: block;
    margin-top: 0.25rem;
    color: #475569;
    font-size: 0.65rem;
    font-weight: 500;
    line-height: 1.35;
  }
}

.summary-target-section {
  padding: 1.275rem 1.05rem;
  animation: summaryRiseIn 0.34s ease-out 0.1s both;
}

.summary-target-status {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.25rem;
}

.summary-status {
  font-size: 0.65rem;
  font-weight: 700;
  line-height: 1.2;
  text-transform: uppercase;
}

.summary-status-warning {
  color: #ea580c;
}

.summary-status-success {
  color: #007d53;
}

.summary-progress-track {
  position: relative;
  width: 100%;
  height: 0.65rem;
  overflow: hidden;
  border-radius: 999px;
  background: #f1f5f9;
}

.summary-progress-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: inherit;
  background: #007d53;
  transform-origin: left center;
  animation: summaryProgressGrow 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.2s both;
  transition: width 0.35s ease;
}

.summary-target-label {
  margin-top: 0.5rem;
  color: #64748b;
  font-size: 0.65rem;
  font-weight: 700;
  line-height: 1.2;
}

.summary-insight-box {
  position: relative;
  margin: 0 1.05rem 1.05rem;
  padding: 0.85rem;
  border: 1px solid rgba(0, 125, 83, 0.1);
  border-radius: 10px;
  background: rgba(0, 125, 83, 0.05);
  animation: summaryRiseIn 0.34s ease-out 0.15s both;
}

.summary-insight-icon {
  position: absolute;
  top: -0.75rem;
  left: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border: 1px solid rgba(0, 125, 83, 0.1);
  border-radius: 6px;
  background: #fff;
  color: #007d53;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
  animation: summaryIconPop 0.38s cubic-bezier(0.16, 1, 0.3, 1) 0.26s both;
}

.summary-insight-box ul {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  padding: 0;
  margin: 0.25rem 0 0;
  list-style: none;
}

.summary-insight-box li {
  display: flex;
  align-items: flex-start;
  gap: 0.425rem;
  color: #334155;

  span {
    flex: 0 0 auto;
    color: #007d53;
    font-size: 0.875rem;
    line-height: 1.5;
  }

  p {
    margin: 0;
    color: #334155;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.5;
    letter-spacing: 0;
  }
}

.summary-card-footer {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  padding: 0 1.05rem 1.05rem;
  animation: summaryRiseIn 0.34s ease-out 0.2s both;

  p {
    margin: 0 0 0.5rem;
    color: #94a3b8;
    font-size: 0.65rem;
    font-weight: 700;
    line-height: 1.2;
    text-transform: uppercase;
  }
}

.summary-chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.425rem;
}

.summary-chip {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 0.1rem 0.425rem;
  border-radius: 4px;
  font-size: 0.65rem;
  line-height: 1.35;
  overflow-wrap: anywhere;
  transition: transform 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(15, 23, 42, 0.08);
  }
}

.summary-chip-muted {
  border: 1px solid #e2e8f0;
  background: #f1f5f9;
  color: #475569;
}

.summary-chip-accent {
  background: rgba(0, 125, 83, 0.1);
  color: #007d53;
  font-weight: 500;
}

.summary-exit-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.425rem;
  width: 100%;
  padding: 0.65rem 0.85rem;
  border: 0;
  border-radius: 8px;
  background: #007d53;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #006644;
    transform: translateY(-1px);
  }
}

@keyframes summaryEntrance {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.98);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes summaryRiseIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes summaryProgressGrow {
  from {
    transform: scaleX(0);
  }

  to {
    transform: scaleX(1);
  }
}

@keyframes summaryIconPop {
  from {
    opacity: 0;
    transform: translateY(4px) scale(0.78);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes summaryPulse {
  50% {
    transform: scale(1.04);
  }
}

@keyframes summaryShimmer {
  0% {
    background-position: 120% 0;
  }

  100% {
    background-position: -120% 0;
  }
}

.section-block {
  margin-bottom: 1.25rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #348481;
  margin: 0 0 0.5rem 0;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.overview-section .section-title {
  font-size: 1rem;
  color: #1a1a1a;
}

.section-content {
  font-size: 0.95rem;
  line-height: 1.7;
  color: #4a4a4a;
}

.overview-section .section-content {
  font-size: 1.05rem;
  font-weight: 500;
  color: #1a1a1a;
  line-height: 1.8;
}

.sub-item {
  margin: 0.35rem 0;
  padding-left: 0.5rem;
}

.sub-item strong {
  color: #333;
}

.summary-paragraph {
  font-size: 1.15rem;
  font-weight: 600;
  line-height: 1.7;
  color: #1a1a1a;
}

.detailed-content {
  font-size: 0.95rem;
  line-height: 1.7;
  color: #4a4a4a;
  margin-top: 1rem;
}

.sn-footer {
  padding: 1.25rem 1.5rem;
}

.action-btn {
  width: 100%;
  background-color: #357B56;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.875rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #2a6b69;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(53, 123, 86, 0.5);
  }
}

/* Transition animations */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-active .smart-narrative-popup,
.modal-fade-leave-active .smart-narrative-popup {
  transition: transform 0.2s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter .smart-narrative-popup,
.modal-fade-leave-to .smart-narrative-popup {
  transform: scale(0.95);
}
</style>
